"use client";

import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import AsyncSelect from "react-select/async";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import api from "../../services/api";
import PaginationBarComp from "../Pagination/PaginationBar";

/* -------------------- Async Selects -------------------- */

const UserSelect = ({ value, onChange }) => {
  const loadOptions = async (input) => {
    if (!input || input.length < 2) return [];
    const { data } = await api.get("/admin/users", {
      params: { search: input, perPage: 20 },
    });
    return data.items.map((u) => ({
      value: u.id,
      label: `${u.name} ${u.family} (${u.mobile || "-"})`,
    }));
  };

  return (
    <AsyncSelect
      cacheOptions
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      isClearable
      placeholder="انتخاب کاربر"
      styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
    />
  );
};

const StoreSelect = ({ value, onChange }) => {
  const loadOptions = async (input) => {
    // حداقل 2 کاراکتر برای سرچ
    if (!input || input.length < 2) return [];

    const { data } = await api.get("/admin/stores", {
      params: { search: input, perPage: 20, },
    });

    const items = data.items ?? data;

    return items.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  };

  return (
    <AsyncSelect
      cacheOptions
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      isClearable
      placeholder="انتخاب فروشگاه"
      noOptionsMessage={() => "موردی یافت نشد"}
      styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
    />
  );
};

/* -------------------- Main Component -------------------- */

const AdminCreditRequestsComp = () => {
  /* pagination */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  /* filters */
  const [status, setStatus] = useState("");
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credit-requests", {
        params: {
          page,
          perPage,
          status: status || undefined,
          userId: user?.value,
          storeId: store?.value,
        },
      });

      setItems(data.items);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      toast.error("خطا در دریافت لیست درخواست‌ها");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, perPage, status, user, store]);

  /* -------------------- Actions -------------------- */

  const approve = async (id) => {
    const res = await Swal.fire({
      title: "تأیید درخواست",
      text: "آیا مطمئن هستید؟",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "بله، تأیید شود",
      cancelButtonText: "انصراف",
    });

    if (!res.isConfirmed) return;

    try {
      await api.post(`/admin/credit-requests/${id}/approve`);
      toast.success("درخواست تأیید شد");
      loadData();
    } catch {
      toast.error("خطا در تأیید درخواست");
    }
  };

  const reject = async (id) => {
    const { value } = await Swal.fire({
      title: "رد درخواست",
      input: "textarea",
      inputLabel: "دلیل رد (اختیاری)",
      showCancelButton: true,
      confirmButtonText: "رد شود",
      cancelButtonText: "انصراف",
    });

    try {
      await api.post(`/admin/credit-requests/${id}/reject`, {
        comment: value,
      });
      toast.success("درخواست رد شد");
      loadData();
    } catch {
      toast.error("خطا در رد درخواست");
    }
  };

  /* -------------------- Render -------------------- */

  return (
    <Card className="border-0 rounded-3">
      <Card.Body>
        <h5 className="mb-3 fw-semibold">درخواست‌های اعتبار (ادمین)</h5>

        {/* Filters */}
        <Row className="mb-3">
          <Col md={3}>
            <Form.Select
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="PENDING">در انتظار</option>
              <option value="APPROVED">تأیید شده</option>
              <option value="REJECTED">رد شده</option>
            </Form.Select>
          </Col>
          <Col md={4}>
            <UserSelect value={user} onChange={(v) => { setPage(1); setUser(v); }} />
          </Col>
          <Col md={4}>
            <StoreSelect value={store} onChange={(v) => { setPage(1); setStore(v); }} />
          </Col>
        </Row>

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>کاربر</th>
                <th>فروشگاه</th>
                <th>کیف پول</th>
                <th>اعتبار</th>
                <th>ایجاد کننده</th>
                <th>تایید کننده</th>
                <th>تاریخ</th>
                <th>وضعیت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {items.length ? (
                items.map((r, i) => (
                  <tr key={r.id}>
                    <td>{(page - 1) * perPage + i + 1}</td>
                    <td>{r.user?.name} {r.user?.family}</td>
                    <td>{r.store?.name}</td>
                    <td>{Number(r.walletAmount).toLocaleString()}</td>
                    <td>{Number(r.creditAmount).toLocaleString()}</td>
                    <td>{r.createdBy ? `${r.createdBy.name} ${r.createdBy.family}` : "-"}</td>
                    <td>{r.statusBy ? `${r.statusBy.name} ${r.statusBy.family}` : "-"}</td>
                    <td>{new Date(r.createdAt).toLocaleDateString("fa-IR")}</td>
                    <td>
                      {r.status === "PENDING" && <Badge bg="warning">در انتظار</Badge>}
                      {r.status === "APPROVED" && <Badge bg="success">تأیید</Badge>}
                      {r.status === "REJECTED" && <Badge bg="danger">رد</Badge>}
                    </td>
                    <td>
                      {r.status === "PENDING" && (
                        <>
                          <Button
                            size="sm"
                            variant="success"
                            className="me-2"
                            onClick={() => approve(r.id)}
                          >
                            تأیید
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => reject(r.id)}
                          >
                            رد
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    موردی یافت نشد
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        <PaginationBarComp
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            perPage={perPage}
            setPerPage={setPerPage}
        />
      </Card.Body>
    </Card>
  );
};

export default AdminCreditRequestsComp;
